import React, { useState } from 'react';
import { useForm } from "antd/lib/form/Form";
import { useTranslation } from "react-i18next";
import { Button, DatePicker, Form, message, Modal, Select, Spin } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/root/root-state.interface";
import { dateFormat } from "../../../utils";

interface IProps {
  getReport: (payload: {
	periodStart: string;
	periodEnd: string;
	language: string;
	governmentAlias: string | null;
  }) => Promise<Blob>;
  show: boolean;
  onHide: () => void;
}

const ReportMovableProperty = (props: IProps) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs|null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const authState = useSelector((state: RootState) => state.authSlice);

  const languages = [
	{ key: "ky", label: "Кыргызский" },
	{ key: "ru", label: "Русский" }
  ];

  const handleSubmit = async () => {
	try {
	  const values = await form.validateFields();
	  setLoading(true);

	  const payload = {
		periodStart: dayjs().format('YYYY-MM-DD'),
		periodEnd: dayjs().format('YYYY-MM-DD'),
		language: selectedLanguage,
		governmentAlias: authState.governmentAlias
	  }

	  const blob = await props.getReport(payload);


	  const url = window.URL.createObjectURL(blob);
	  const link = document.createElement("a");
	  link.href = url;
	  link.setAttribute(
		"download",
		`Report_${ payload }_${ dayjs().format("YYYY-MM-DD") }.xlsx`
	  );
	  document.body.appendChild(link);
	  link.click();
	  document.body.removeChild(link);

	  message.success(t('reports.download_success'));
	} catch (error: any) {
	  Modal.error({
		title: t("msg.error"),
		content: error.message || t('reports.download_failed'),
	  });
	} finally {
	  setLoading(false);
	}
  };

  const handleHide = () => {
	form.resetFields();
	setStartDate(null);
	props.onHide();
  };

  return (
	<Modal
	  title={ t('reports.report_by_movable_property') }
	  open={ props.show }
	  onCancel={ handleHide }
	  onOk={ handleSubmit }
	  okText={ t('general.create') }
	  width={ 700 }
	  footer={ null }
	>
	  <Spin spinning={ loading } tip={ t('reports.loading') }>
		<Form form={ form } layout="vertical" autoComplete="off">
		  <div style={ { display: "flex", gap: "10px" } }>
			<Form.Item
			  label={ t('general.start_date_period') }
			  name='periodStart'
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <DatePicker
				format={ dateFormat }
				placeholder={ t('general.select_date') }
				onChange={ (date) => setStartDate(date) }/>
			</Form.Item>
			<Form.Item
			  label={ t('general.end_date_period') }
			  name='periodEnd'
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <DatePicker
				format={ dateFormat }
				placeholder={ t('general.select_date') }
				disabledDate={ (current) =>
				  !!startDate && current.isBefore(startDate.add(1, "day"), "day")
				}
			  />
			</Form.Item>
			<Form.Item
			  label={ t('reports.select_language') }
			  name="select_language"
			  rules={ [{ required: true, message: t('msg.fill_in_the_field') }] }
			>
			  <Select
				value={ selectedLanguage }
				onChange={ setSelectedLanguage }
				style={ { width: "200px" } }
				placeholder={ t('reports.select_language') }
			  >
				{ languages.map((lang) => (
				  <Select.Option key={ lang.key } value={ lang.key }>
					{ lang.label }
				  </Select.Option>
				)) }
			  </Select>
			</Form.Item>
		  </div>
		  <div style={ { textAlign: "right", marginTop: "20px" } }>
			<Button type="primary" block onClick={ handleSubmit } style={ { width: "200px" } }>
			  { t('reports.download_report') }
			</Button>
		  </div>
		</Form>
	  </Spin>
	</Modal>
  );
};

export default ReportMovableProperty;
